import React, { useImperativeHandle, useState } from 'react'
import Alert from '@zera-admin/alert'
import Spinner from '@zera-admin/spinner'
import { Column, Container, Row } from '@zera-admin/page'

import queryService from 'services/http/bi-tool/query'
import {
  Widget,
  WidgetHandlerRef,
  WidgetVisualization,
} from 'services/http/bi-tool/widget'
import Bars from 'widgets/bars'

import { getCriteriaInterval } from '../visualization/getters'
import { mapQueryForBar } from './utils/mappers'
import * as Styled from './Bar.styled'
import { BarProps, BarValueProps, BarVisualizationOptions } from './types'

const Bar: React.ForwardRefRenderFunction<
  WidgetHandlerRef<BarVisualizationOptions>,
  BarProps
> = (props, ref) => {
  const [isLoading, setIsLoading] = useState(true)
  const [values, setValues] = useState<BarValueProps[]>([])
  const [error, setError] = useState<string>()

  useImperativeHandle(ref, () => ({
    runQuery,
  }))

  const getColumn = () => {
    const { grid } = props.widget

    if (grid) {
      if (grid === 2) return 6
      else if (grid === 3) return 4
      else if (grid === 4) return 3
      else if (grid === 6) return 2
    }

    return 12
  }

  const runQuery = (widget: Widget) => {
    setIsLoading(true)

    queryService
      .run(
        widget?.query,
        widget?.visualization as WidgetVisualization,
        widget.type,
        {
          criteriaType: props.widget.criteria,
          dateType: getCriteriaInterval(props.widget),
        }
      )
      .then((res) => {
        setValues(mapQueryForBar(res.data, widget.visualization?.options))
        setIsLoading(false)

        if (error) {
          setError('')
        }
      })
      .catch((err) => {
        setIsLoading(false)
        setValues([])
        setError(
          err?.data?.errorMessage ||
            'Sorgunuzda bir hata bulunmaktadır. Lütfen sorgunuzu kontrol ediniz.'
        )
      })
  }

  const renderContent = () => {
    const { grid } = props.widget

    if (grid) {
      const column = Math.round(values.length / grid)
      const group: { [key: string]: BarValueProps[] } = {}

      for (var i = 0; i < values.length; i += column) {
        group[i] = values.slice(i, i + column)
      }

      return (
        <Row>
          {Object.keys(group).map((key) => {
            const value = group[key]

            return (
              <Column xs={{ size: getColumn() }}>
                <Bars bars={value} />
              </Column>
            )
          })}
        </Row>
      )
    } else {
      return (
        <Container width="narrow">
          <Styled.Bar theme={props.widget.visualization?.theme}>
            <Bars bars={values} />
          </Styled.Bar>
        </Container>
      )
    }
  }

  const renderChart = () => {
    if (isLoading) {
      return (
        <Row xs={{ align: 'center', justify: 'center' }}>
          <Spinner className="spinner" size="medium" />
        </Row>
      )
    } else {
      if (values.length) {
        return renderContent()
      } else {
        return (
          <Alert
            appearance={error ? 'error' : 'warning'}
            title="Bar oluşturulamadı"
          >
            {error ||
              'Barı oluşturacak veri bulunamadı. Lütfen sorgularınızı ve bar ayarlarınızı kontrol ediniz.'}
          </Alert>
        )
      }
    }
  }

  return renderChart()
}

export default React.forwardRef(Bar)
